// Aba "Painel de trocas" da tela Dispositivos: uma linha por paciente + dispositivo com tudo
// junto (IW + Quality). Regras em ./regrasTrocas.js.
import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { supabase } from '../supabase';
import { db, auth, getCollectionName } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, where } from '../firestore-compat';
import { montarPainel, FAMILIAS } from './regrasTrocas';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#475569', primary: '#4f46e5', soft: '#eef2ff' };
const POR_PAGINA = 50;

async function api(rota) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(rota, { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}
const fmt = d => (d ? d.toLocaleDateString('pt-BR') : '—');
const chip = (fundo, cor) => ({ fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });

export default function PainelTrocas({ solicitacoes = [], canulas = [] }) {
  const [iw, setIw] = useState(null);
  const [aditivos, setAditivos] = useState([]);
  const [trocas, setTrocas] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [unidade, setUnidade] = useState('Todas');
  const [situacao, setSituacao] = useState('');
  const [local, setLocal] = useState('');
  const [familia, setFamilia] = useState('');
  const [soIW, setSoIW] = useState(false);
  const [verForaAtendimento, setVerForaAtendimento] = useState(false);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [salvando, setSalvando] = useState('');

  const [perfil, setPerfil] = useState({ roles: [], perms: {}, nome: '' });
  const usuario = auth.currentUser;
  useEffect(() => {
    if (!usuario?.email) return;
    getDocs(query(collection(db, getCollectionName('users')), where('email', '==', usuario.email.toLowerCase())))
      .then(s => { const u = s.docs[0]?.data() || {}; setPerfil({ roles: Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []), perms: u.permissions || {}, nome: u.name || usuario.email }); })
      .catch(() => {});
  }, [usuario]);
  const podeEditar = usuario?.email === 'alexandrebareaoliveira@gmail.com' || perfil.roles.includes('admin') || perfil.roles.includes('administrador')
    || perfil.perms?.controle_dispositivos?.edit === true || perfil.perms?.solicitacao_troca?.edit === true;

  const carregar = async () => {
    setCarregando(true); setErro('');
    try {
      const [dadosIW, ped, snap] = await Promise.all([
        api('/api/iw-pacientes'),
        api('/api/iw-pedidos').catch(() => ({ aditivos: [] })),
        getDocs(collection(db, getCollectionName('trocas_dispositivos'))),
      ]);
      setIw(dadosIW); setAditivos(ped.aditivos || []); setTrocas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);
  useEffect(() => { setPagina(1); }, [unidade, situacao, local, familia, soIW, verForaAtendimento, busca]);

  const todas = useMemo(() => (iw ? montarPainel({ iw, trocas, solicitacoes, canulas, aditivos }) : []), [iw, trocas, solicitacoes, canulas, aditivos]);
  const base = useMemo(() => todas.filter(l => (verForaAtendimento ? (!l.emAtendimento || l.naoUsaMais) : (l.emAtendimento && !l.naoUsaMais))
    && (unidade === 'Todas' || l.unidade === unidade)), [todas, verForaAtendimento, unidade]);
  const q = busca.trim().toLowerCase();
  const filtradas = useMemo(() => base.filter(l =>
    (!situacao || l.situacao.id === situacao)
    && (!local || (local === 'definir' ? !l.local : l.local === local))
    && (!familia || l.familia === familia)
    && (!soIW || l.soNoIW)
    && (!q || l.nome.toLowerCase().includes(q) || String(l.numIW).includes(q))
  ).sort((a, b) => (a.dias ?? 99999) - (b.dias ?? 99999)), [base, situacao, local, familia, soIW, q]);

  const conta = f => base.filter(f).length;
  const definirLocal = async (l, valor) => {
    if (!l.idRegistro) return;
    setSalvando(l.chave);
    try {
      await updateDoc(doc(db, getCollectionName('trocas_dispositivos'), l.idRegistro), { local_proxima_troca: valor, local_definido_por: perfil.nome || usuario?.email || '', local_definido_em: new Date().toISOString() });
      setTrocas(ts => ts.map(t => (t.id === l.idRegistro ? { ...t, local_proxima_troca: valor } : t)));
    } catch (e) { alert('Não foi possível salvar: ' + e.message); }
    setSalvando('');
  };

  if (carregando) return <div style={{ color: P.muted, padding: 20 }}>Carregando IW e Quality…</div>;
  if (erro) return <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px' }}>Não foi possível carregar: {erro}</div>;

  const cartao = (id, rotulo, valor, cor, fundo, ativo, onClick) => (
    <button key={id} onClick={onClick} style={{ textAlign: 'left', background: ativo ? fundo : P.white, border: `1px solid ${ativo ? cor : P.border}`, borderRadius: 12, padding: '10px 14px', cursor: 'pointer', minWidth: 130 }}>
      <div style={{ fontSize: '0.74rem', fontWeight: 600, color: P.muted }}>{rotulo}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: cor }}>{valor}</div>
    </button>
  );
  const th = { padding: '9px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.84rem' };
  const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const atualizado = iw?.evolucoesAtualizadoEm ? new Date(iw.evolucoesAtualizadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: '0.8rem', color: P.muted }}>
        Junta o IW (pacientes em atendimento, pedidos e trocas lançados no template, aditivos) com o Quality (pedidos de troca hospitalar e cânulas em casa). Exportação do IW de {atualizado}.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {cartao('atrasada', '🔴 Atrasadas', conta(l => l.situacao.id === 'atrasada'), '#991b1b', '#fee2e2', situacao === 'atrasada', () => setSituacao(situacao === 'atrasada' ? '' : 'atrasada'))}
        {cartao('ate7', '🟠 Vencem em 7 dias', conta(l => l.situacao.id === 'ate7'), '#9a3412', '#ffedd5', situacao === 'ate7', () => setSituacao(situacao === 'ate7' ? '' : 'ate7'))}
        {cartao('ate30', '🟡 Vencem em 30 dias', conta(l => l.situacao.id === 'ate30'), '#854d0e', '#fef9c3', situacao === 'ate30', () => setSituacao(situacao === 'ate30' ? '' : 'ate30'))}
        {cartao('emDia', '🟢 Em dia', conta(l => l.situacao.id === 'emDia'), '#166534', '#dcfce7', situacao === 'emDia', () => setSituacao(situacao === 'emDia' ? '' : 'emDia'))}
        {cartao('semData', 'Sem data', conta(l => l.situacao.id === 'semData'), '#475569', '#f1f5f9', situacao === 'semData', () => setSituacao(situacao === 'semData' ? '' : 'semData'))}
        <span style={{ width: 1, background: P.border, margin: '0 4px' }} />
        {cartao('casa', '🏠 Em casa', conta(l => l.local === 'Domiciliar'), '#166534', '#dcfce7', local === 'Domiciliar', () => setLocal(local === 'Domiciliar' ? '' : 'Domiciliar'))}
        {cartao('hosp', '🏥 Hospitalar', conta(l => l.local === 'Hospitalar'), '#1e3a8a', '#dbeafe', local === 'Hospitalar', () => setLocal(local === 'Hospitalar' ? '' : 'Hospitalar'))}
        {cartao('definir', 'A definir', conta(l => !l.local), '#475569', '#f1f5f9', local === 'definir', () => setLocal(local === 'definir' ? '' : 'definir'))}
        <span style={{ width: 1, background: P.border, margin: '0 4px' }} />
        {cartao('soiw', '🗂 Pedido no IW, falta no Quality', conta(l => l.soNoIW), '#065f46', '#ecfdf5', soIW, () => setSoIW(!soIW))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <label style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente ou nº IW</span>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar paciente ou nº IW" style={{ padding: '8px 10px 8px 30px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.84rem', width: 220 }} />
        </label>
        <select value={familia} onChange={e => setFamilia(e.target.value)} style={{ padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.84rem' }}>
          <option value="">Todos os dispositivos</option>
          {Object.entries(FAMILIAS).map(([k, f]) => <option key={k} value={k}>{f.nome}</option>)}
        </select>
        <select value={unidade} onChange={e => setUnidade(e.target.value)} style={{ padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.84rem' }}>
          {['Todas', 'VitalCare', 'HomeBaby'].map(u => <option key={u} value={u}>{u === 'Todas' ? 'Todas as unidades' : u === 'VitalCare' ? 'Vitalcare' : 'Home Baby'}</option>)}
        </select>
        <label style={{ fontSize: '0.8rem', color: P.muted, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={verForaAtendimento} onChange={e => setVerForaAtendimento(e.target.checked)} /> Ver quem teve alta ou não usa mais
        </label>
        <button onClick={carregar} title="Recarregar" aria-label="Recarregar" style={{ marginLeft: 'auto', border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex' }}><RefreshCw size={15} /></button>
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
            <thead><tr>{['Paciente', 'Dispositivo', 'Onde troca', 'Última troca', 'Próxima troca', 'Situação', 'Próxima troca: etapa', 'Aditivo no IW'].map(hd => <th key={hd} style={th}>{hd}</th>)}</tr></thead>
            <tbody>
              {filtradas.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: P.muted, padding: 28 }}>Nenhum paciente neste filtro.</td></tr>}
              {filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA).map(l => (
                <tr key={l.chave} style={{ background: l.soNoIW ? '#f0fdf4' : undefined }}>
                  <td style={{ ...td, fontWeight: 700 }}>
                    {l.nome}
                    <div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500 }}>{[l.numIW && `IW ${l.numIW}`, l.unidade !== '—' && (l.unidade === 'VitalCare' ? 'Vitalcare' : l.unidade === 'HomeBaby' ? 'Home Baby' : l.unidade), l.operadora, l.liminar && 'liminar'].filter(Boolean).join(' · ')}</div>
                    {l.naoUsaMais && <div style={{ fontSize: '0.72rem', color: '#9a3412' }}>IW diz que não usa mais</div>}
                  </td>
                  <td style={td}>{l.dispositivo}<div style={{ fontSize: '0.72rem', color: P.muted }}>{FAMILIAS[l.familia]?.curto}</div></td>
                  <td style={td}>
                    {podeEditar && l.idRegistro ? (
                      <label>
                        <span style={{ position: 'absolute', left: -9999 }}>Onde será a próxima troca de {l.dispositivo} de {l.nome}</span>
                        <select value={l.localManual || ''} disabled={salvando === l.chave} onChange={e => definirLocal(l, e.target.value)}
                          style={{ fontSize: '0.78rem', padding: '4px 6px', borderRadius: 8, border: `1px solid ${P.border}`, background: l.local === 'Hospitalar' ? '#dbeafe' : l.local === 'Domiciliar' ? '#dcfce7' : P.white }}>
                          <option value="">{l.local && !l.localManual ? `${l.local === 'Domiciliar' ? 'Casa' : 'Hospital'} (${l.localOrigem})` : 'A definir'}</option>
                          <option value="Domiciliar">🏠 Casa</option>
                          <option value="Hospitalar">🏥 Hospital</option>
                        </select>
                      </label>
                    ) : (
                      <span style={chip(l.local === 'Hospitalar' ? '#dbeafe' : l.local === 'Domiciliar' ? '#dcfce7' : '#f1f5f9', l.local === 'Hospitalar' ? '#1e3a8a' : l.local === 'Domiciliar' ? '#166534' : '#475569')}>
                        {l.local === 'Hospitalar' ? '🏥 Hospital' : l.local === 'Domiciliar' ? '🏠 Casa' : 'A definir'}
                      </span>
                    )}
                  </td>
                  <td style={td}>{fmt(l.ultima)}<div style={{ fontSize: '0.7rem', color: P.muted }}>{l.origemUltima && `fonte: ${l.origemUltima}`}</div></td>
                  <td style={td}>{fmt(l.proxima)}{l.proximaCalculada && l.proxima && <div style={{ fontSize: '0.7rem', color: P.muted }}>calculada ({l.periodo} dias)</div>}</td>
                  <td style={td}><span style={chip(l.situacao.fundo, l.situacao.cor)}>{l.situacao.rotulo}</span></td>
                  <td style={{ ...td, fontSize: '0.78rem' }}>
                    <Etapas l={l} />
                  </td>
                  <td style={{ ...td, fontSize: '0.78rem' }}>
                    {l.aditivo ? (
                      <>
                        <div style={{ fontWeight: 700, color: '#065f46' }}>Sim · {fmt(l.aditivo.quando)}</div>
                        <div style={{ color: P.muted }}>{l.aditivo.item}</div>
                        <div style={{ color: P.muted }}>Autorização interna: {l.aditivo.autorizacaoInterna}/3 · operadora: {l.aditivo.autorizacaoOperadora}</div>
                      </>
                    ) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 14px', borderTop: `1px solid ${P.border}`, fontSize: '0.8rem', color: P.muted, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span>{filtradas.length} linha{filtradas.length === 1 ? '' : 's'} · linhas em verde: pedido de troca no IW que ainda não está no Quality</span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>Anterior</button>
            {pagina} / {paginas}
            <button disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>Próxima</button>
          </span>
        </div>
      </div>
    </div>
  );
}

// Passos da próxima troca: pedido → aditivo/autorização → agendamento
function Etapas({ l }) {
  const passo = (ok, texto, cor = '#065f46') => (
    <div style={{ display: 'flex', gap: 5, alignItems: 'baseline', color: ok ? cor : '#94a3b8' }}>
      <span aria-hidden="true">{ok ? '●' : '○'}</span><span>{texto}</span>
    </div>
  );
  const fmtD = d => (d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '');
  if (!l.pedidoIW && !l.pedidoHosp && !l.aditivo && !l.agendadaCasa) return <span style={{ color: '#94a3b8' }}>Sem pedido ainda</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {passo(!!l.pedidoIW || !!l.pedidoHosp, l.pedidoIW ? `Pedido no IW ${fmtD(l.pedidoIW.quando)}${l.soNoIW ? ' — falta no Quality' : ''}` : l.pedidoHosp ? `Pedido no Quality ${fmtD(l.pedidoHosp.criado)}` : 'Pedido')}
      {passo(!!l.aditivo, l.aditivo ? `Aditivo ${fmtD(l.aditivo.quando)} · interna ${l.aditivo.autorizacaoInterna}/3` : 'Aditivo')}
      {l.pedidoHosp && passo(true, `Hospital: ${l.pedidoHosp.status}${l.pedidoHosp.data ? ` · ${fmtD(l.pedidoHosp.data)}` : ''}${l.pedidoHosp.local ? ` · ${l.pedidoHosp.local}` : ''}`, '#1e3a8a')}
      {l.agendadaCasa && passo(true, `Troca em casa prevista ${fmtD(l.agendadaCasa)}`, '#166534')}
    </div>
  );
}
